import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Send, MessageCircle, ArrowLeft, Phone, MapPin, User, Calendar, Clock, DollarSign, Tag, Gift } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import TagManager from '../components/TagManager';

interface Message {
  id: number;
  phone: string;
  message: string;
  sender: 'user' | 'bot' | 'system';
  createdAt: number;
}

interface Order {
  id: number;
  createdAt: number;
  paymentConfirmed: number;
  paymentProof?: string;
  name: string;
  phone: string;
  region: string;
  address: string;
  addressBuilding?: string;
  addressFloor?: string;
  addressUnit?: string;
  estate: string | null;
  orderType?: string;
  deliveryDate: string;
  deliveryTime?: string;
  items?: string;
  totalPrice: number;
  deliveryFee?: number;
  remarks?: string;
  adminRemarks?: string;
  orderCompleted: number;
  [key: string]: any;
}

const AdminOrderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [customerTags, setCustomerTags] = useState<any[]>([]);
  const [customerOrderCount, setCustomerOrderCount] = useState(0);
  const [customerTotalSpent, setCustomerTotalSpent] = useState(0);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftOptions, setGiftOptions] = useState<{ id: number; key: string; name: string }[]>([]);
  const [selectedGiftKey, setSelectedGiftKey] = useState('');
  const [attachingGift, setAttachingGift] = useState(false);
  const [giftError, setGiftError] = useState('');
  // 阿占隨意插圖發送選項暫時隱藏（展示部份未啟用；後端本來都冇處理呢兩個 flag，UI 純裝飾）
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { can, user } = usePermissions();

  // 客戶身份鍵：聯絡電話同時作為 WhatsApp 號碼使用
  const identityPhone = order?.phone;

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    if (user?.role === 'estate_manager') { navigate('/admin/orders'); return; }
    fetchOrder();
  }, [id]);

  useEffect(() => {
    if (identityPhone) {
      fetchChatHistory(identityPhone);
      fetchCustomerTags(identityPhone);
    }
  }, [order, identityPhone]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchOrder = async () => {
    try {
      const res = await apiFetch(`/api/public/admin/orders/${id}`);
      if (res.ok) { setOrder(await res.json()); }
      else if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); }
    } finally {
      setLoading(false);
    }
  };

  const openGiftModal = async () => {
    setGiftError('');
    setSelectedGiftKey('');
    setShowGiftModal(true);
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) setGiftOptions(data.filter((g: any) => g.isActive));
      }
    } catch { /* ignore */ }
  };

  const handleAttachGift = async () => {
    if (!selectedGiftKey) { setGiftError('請選擇禮品'); return; }
    setAttachingGift(true);
    setGiftError('');
    try {
      const res = await apiFetch(`/api/public/admin/orders/${id}/gifts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giftKey: selectedGiftKey }),
      });
      const data = await res.json();
      if (!res.ok) { setGiftError(data.error || '附加失敗'); return; }
      setShowGiftModal(false);
      await fetchOrder();
    } catch {
      setGiftError('網絡錯誤，請重試');
    } finally {
      setAttachingGift(false);
    }
  };

  const fetchChatHistory = async (phone: string) => {
    setChatLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/whatsapp/conversations/${phone}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { console.error('Failed to fetch chat:', e); }
    finally { setChatLoading(false); }
  };

  const fetchCustomerTags = async (phone: string) => {
    try {
      const res = await apiFetch(`/api/public/admin/customers/${phone}`);
      if (res.ok) {
        const data = await res.json();
        setCustomerTags(data.manualTags || []);
        setCustomerOrderCount((data.orders || []).length);
        setCustomerTotalSpent(
          (data.orders || []).reduce((sum: number, o: any) => sum + (o.total_price || 0), 0)
        );
      }
    } catch (e) { console.error('Failed to fetch customer tags:', e); }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order) return;
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ adminRemarks: order.adminRemarks })
    });
    if (res.ok) { alert('更新成功'); navigate('/admin/orders'); }
    else { alert('更新失敗'); }
  };

  const handleConfirmPayment = async () => {
    if (!order) return;
    if (!confirm('確認已收到此訂單的付款？')) return;
    setConfirming(true);
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paymentConfirmed: 1 })
    });
    if (res.ok) {
      setOrder({ ...order, paymentConfirmed: 1 });
      alert('付款已確認！客戶將收到 WhatsApp 通知。');
    } else { alert('確認失敗'); }
    setConfirming(false);
  };

  const handleCompleteOrder = async () => {
    if (!order) return;
    if (!confirm('確認標記此訂單為已完成？')) return;
    setCompleting(true);
    const res = await apiFetch(`/api/public/admin/orders/${order.id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    if (res.ok) {
      alert('訂單已標記為完成');
      await fetchOrder();
    } else {
      alert('標記失敗');
    }
    setCompleting(false);
  };

  const isWindowOpen = () => {
    if (!messages.length) return false;
    const lastUserMessage = [...messages]
      .filter(m => m.sender === 'user')
      .sort((a, b) => b.createdAt - a.createdAt)[0];
    if (!lastUserMessage) return false;
    const twentyFourHoursAgo = Math.floor(Date.now() / 1000) - 24 * 60 * 60;
    return lastUserMessage.createdAt >= twentyFourHoursAgo;
  };

  const handleSendMessage = async () => {
    if (!identityPhone || !replyText.trim()) return;
    setSendLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: identityPhone, message: replyText.trim() })
      });
      if (res.ok) {
        setReplyText('');
        await fetchChatHistory(identityPhone);
      } else { alert('發送失敗'); }
    } catch (e) { alert('發送失敗'); }
    finally { setSendLoading(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  if (loading) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
    </AdminLayout>
  );
  if (!order) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="text-center text-gray-500 dark:text-gray-400 py-12">訂單不存在</div>
    </AdminLayout>
  );

  const orderItems = (() => {
    try { return JSON.parse(order.items || '[]'); } catch { return []; }
  })();

  const autoTags = (() => {
    const tags: { label: string; color: string }[] = [];
    if (customerOrderCount === 1) tags.push({ label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200' });
    else if (customerOrderCount >= 2 && customerOrderCount < 5) tags.push({ label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200' });
    if (Number(order?.totalPrice || 0) >= 500) tags.push({ label: '大單 （累計≥$500)', color: 'bg-amber-100 text-amber-700 border-amber-200' });
    return tags.map((t, i) => (
      <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${t.color}`}>
        {t.label}
      </span>
    ));
  })();

  return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/orders')} className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> 返回訂單列表
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">訂單 #{(order.orderNum || "-")}</h1>
          {order.orderCompleted === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">訂單完成</span>
          ) : order.paymentConfirmed === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">已付款 / 處理中</span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">待付款</span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">下單時間：{new Date(order.createdAt * 1000).toLocaleString('zh-HK')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: Order Info (3/5) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Payment Proof */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              付款記錄
            </h2>
            {order.paymentProof ? (
              <div className="mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">付款憑證：</p>
                <a href={order.paymentProof} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline break-all text-sm">
                  {order.paymentProof}
                </a>
              </div>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 text-sm italic mb-4">暫無付款憑證</p>
            )}

            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              {can(PERMISSIONS.ORDERS_WRITE) ? (
                <>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                        checked={order.paymentConfirmed === 1}
                        disabled={confirming}
                        onChange={async (e) => {
                          const newValue = e.target.checked ? 1 : 0;
                          if (newValue === 1 && !confirm('確認已收到付款？系統將發送 WhatsApp 通知給客戶。')) return;
                          setConfirming(true);
                          const res = await apiFetch(`/api/public/admin/orders/${id}`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paymentConfirmed: newValue })
                          });
                          if (res.ok) { setOrder({...order, paymentConfirmed: newValue}); }
                          else { alert('更新失敗'); e.target.checked = !e.target.checked; }
                          setConfirming(false);
                        }}
                      />
                      <span className={`font-bold ${order.paymentConfirmed ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        {order.paymentConfirmed ? '已確認付款' : '未確認付款'}
                      </span>
                    </label>
                    {!order.paymentConfirmed && (
                      <button
                        onClick={handleConfirmPayment}
                        disabled={confirming}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {confirming ? '處理中...' : '一鍵確認付款'}
                      </button>
                    )}
                  </div>
                  {/* KPay 退款：只對經 KPay 付款且已確認的訂單顯示（成功後自動沖銷佣獎/釋放集運） */}
                  {(order as any).kpayTxn && order.paymentConfirmed === 1 && (
                    <button
                      onClick={async () => {
                        const txn = (order as any).kpayTxn;
                        if (!confirm(`經 KPay 退款 HK$${txn.amount}（交易 ${txn.kpayOrderNo || '—'}）？\n退款成功後訂單會取消付款確認，佣金/獎勵會自動沖銷。`)) return;
                        setConfirming(true);
                        try {
                          const res = await apiFetch(`/api/public/admin/orders/${id}/kpay/refund`, { method: 'POST' });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.success) {
                            alert(`退款已發起（KPay 結果代碼 ${data.kpayResult}）`);
                            setOrder({ ...order, paymentConfirmed: 0 });
                          } else {
                            alert(`退款失敗：${data.error || res.status}`);
                          }
                        } finally {
                          setConfirming(false);
                        }
                      }}
                      disabled={confirming}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 transition-colors w-fit"
                    >
                      KPay 退款 HK${(order as any).kpayTxn.amount}
                    </button>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${order.paymentConfirmed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {order.paymentConfirmed ? '已確認付款' : '未確認付款'}
                  </span>
                  <span className="text-xs text-gray-400">無權限更新付款狀態</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Info (Read Only) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
              訂單資料（只讀）
              {order.orderType === 'pickup' && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-cyan-100 text-cyan-700 border border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-300 dark:border-cyan-800 align-middle">🏪 門市自取</span>
              )}
              {order.orderType === 'sf_cold_chain' && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 align-middle">🚚 額外運費</span>
              )}
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> 姓名</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.name}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> 聯絡電話（WhatsApp）</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                  <div className="font-medium">{identityPhone}</div>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {order.orderType === 'pickup' ? '自取地點' : '地址'}</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                  {order.orderType !== 'pickup' && `${order.region} `}{(() => {
                    const building = order.addressBuilding ? `${order.addressBuilding}座` : '';
                    const floor = order.addressFloor ? `${order.addressFloor}樓` : '';
                    const unit = order.addressUnit ? `${order.addressUnit}室` : '';
                    const formatted = `${building}${floor}${unit}`;
                    return formatted || order.address || '-';
                  })()}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {order.orderType === 'pickup' ? '取貨方式' : '屋苑'}</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.estate || '-'}</div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {order.orderType === 'pickup' ? '取貨日期' : '配送日期'}</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.deliveryDate}</div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">客戶備註（只讀）</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{order.remarks || '—'}</div>
              </div>
            </div>

            {/* Customer Tags */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> 客戶標籤
                </label>
                {can(PERMISSIONS.CUSTOMERS_WRITE) && (
                  <button
                    type="button"
                    onClick={() => setShowTagModal(true)}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1 border border-purple-200 rounded hover:bg-purple-50 transition"
                  >
                    管理標籤
                  </button>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  {autoTags}
                  {customerTags.map((t: any) => (
                    <span key={t.id} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border bg-${t.color}-100 text-${t.color}-700 border-${t.color}-200`}>
                      {t.tag}
                    </span>
                  ))}
                  {customerTags.length === 0 && customerOrderCount === 0 && (
                    <span className="text-xs text-gray-400 italic">暫無標籤</span>
                  )}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">產品明細</label>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                {orderItems.map((item: any, idx: number) => {
                  const itemName = item.name
                    ? item.name
                    : item.packageType
                      ? (item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯')
                      : (item.productId || '商品');
                  return (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{itemName} x {item.quantity || 1}</span>
                      <span className="font-medium">HK${item.subtotal || (item.unitPrice * (item.quantity || 1))}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between text-sm">
                  <span>商品小計</span>
                  <span>HK${order.totalPrice - (order.deliveryFee || 0)}</span>
                </div>
                {Number(order.voucherDiscount || 0) > 0 && (
                  <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                    <span>🎁 回贈券扣減</span>
                    <span>-HK${order.voucherDiscount}</span>
                  </div>
                )}
                {Number(order.deliveryFee || 0) > 0 && (
                  <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
                    <span>🚚 額外運費</span>
                    <span>HK${order.deliveryFee}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between font-bold text-sm">
                  <span>實收</span>
                  <span>HK${order.totalPrice - Number(order.voucherDiscount || 0)}</span>
                </div>
              </div>
            </div>

            {/* Attached Gifts */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Gift className="w-3 h-3" /> 隨單附送禮品
                </label>
                {can(PERMISSIONS.ORDERS_WRITE) && (
                  <button
                    type="button"
                    onClick={openGiftModal}
                    className="text-xs text-orange-600 hover:text-orange-800 font-medium px-2 py-1 border border-orange-200 rounded hover:bg-orange-50 transition"
                  >
                    附加禮品
                  </button>
                )}
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 space-y-1">
                {(order.attachedGifts || []).length === 0 ? (
                  <span className="text-xs text-gray-400 italic">此訂單暫無隨單禮品</span>
                ) : (
                  (order.attachedGifts || []).map((g: any) => (
                    <div key={g.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        🎁 {g.giftName || g.giftKey || g.description}{g.quantity > 1 ? ` x ${g.quantity}` : ''}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${g.status === 'delivered' ? 'bg-green-100 text-green-700' : g.status === 'cancelled' ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'}`}>
                        {g.status === 'delivered' ? '已交付' : g.status === 'cancelled' ? '已取消' : '已隨單'}
                      </span>
                    </div>
                  ))
                )}
              </div>
              {(order.attachedGifts || []).some((g: any) => g.status === 'attached') && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">⚠️ 打包出貨時請記得放入以上禮品，交付後可於「待送禮品」頁標記已交付。</p>
              )}
            </div>

            {/* Admin Editable Section */}
            <form onSubmit={handleUpdate} className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">管理員編輯區</h3>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1 text-red-500">管理員備註</label>
                <textarea
                  className="w-full p-2.5 border border-red-200 bg-red-50 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none disabled:bg-gray-100 disabled:text-gray-500"
                  rows={2}
                  value={order.adminRemarks || ''}
                  disabled={!can(PERMISSIONS.ORDERS_WRITE)}
                  onChange={e => setOrder({...order, adminRemarks: e.target.value})}
                />
              </div>
              {order.paymentConfirmed === 1 && order.orderCompleted === 0 && can(PERMISSIONS.ORDERS_COMPLETE) && (
                <div className="mb-4 space-y-2">
                  <button
                    type="button"
                    onClick={handleCompleteOrder}
                    disabled={completing}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {completing ? '處理中...' : '標記訂單完成'}
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                {can(PERMISSIONS.ORDERS_WRITE) && (
                  <button type="submit" className="bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors">儲存備註</button>
                )}
                <button type="button" onClick={() => navigate('/admin/orders')} className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">返回</button>
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT: WhatsApp Chat (2/5) */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col h-[calc(100vh-180px)] sticky top-6">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-xl">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <div>
                    <h3 className="font-bold text-sm">客戶對話</h3>
                    <p className="text-xs text-orange-100">{identityPhone}</p>
                  </div>
                </div>
                <div className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  isWindowOpen()
                    ? 'bg-green-500/30 text-white'
                    : 'bg-amber-500/30 text-white'
                }`}>
                  {isWindowOpen() ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {isWindowOpen() ? '24h 窗口開啟' : '24h 窗口已關閉'}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {chatLoading ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-8 text-sm">加載對話中...</p>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 dark:text-gray-500 text-sm">暫無對話記錄</p>
                  <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">客戶發送消息後會自動顯示在此</p>
                </div>
              ) : (
                [...messages].reverse().map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      msg.sender === 'user'
                        ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-300 rounded-bl-none'
                        : 'bg-orange-600 text-white rounded-br-none'
                    }`}>
                      <div className="break-words">{msg.message}</div>
                      <div className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-gray-400' : 'text-orange-200'}`}>
                        {msg.createdAt ? new Date(msg.createdAt * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        {msg.sender !== 'user' && <span className="ml-1 opacity-75">· {msg.sender === 'system' ? '系統' : '回覆'}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Send Log */}
            {messages.filter(m => m.sender !== 'user').length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs text-gray-400">
                  已發送 {messages.filter(m => m.sender !== 'user').length} 條消息 ·
                  最後發送：{
                    (() => {
                      const lastBot = [...messages].reverse().find(m => m.sender !== 'user');
                      return lastBot?.createdAt
                        ? new Date(lastBot.createdAt * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-';
                    })()
                  }
                </p>
              </div>
            )}

            {/* Reply Input */}
            {can(PERMISSIONS.CONVERSATIONS_SEND) ? (
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-xl">
                <div className="flex gap-2">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="輸入消息通知客戶..."
                    rows={2}
                    className="flex-1 p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none bg-white dark:bg-gray-800 dark:text-white"
                    disabled={sendLoading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendLoading || !replyText.trim()}
                    className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">按 Enter 發送 · Shift+Enter 換行</p>
              </div>
            ) : (
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl text-center">
                <p className="text-xs text-gray-400">無權限發送 WhatsApp 消息</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tag Manager Modal */}
      {showTagModal && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {identityPhone && (
                <TagManager
                  phone={identityPhone}
                  name={order.name}
                  orderCount={customerOrderCount}
                  totalSpent={customerTotalSpent}
                  manualTags={customerTags}
                  onTagsChange={() => {
                    fetchCustomerTags(identityPhone);
                  }}
                />
              )}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTagModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attach Gift Modal */}
      {showGiftModal && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-orange-600" /> 附加隨單禮品
            </h3>
            {giftOptions.length === 0 ? (
              <p className="text-sm text-amber-600 dark:text-amber-400">尚未建立啟用中的禮品，請先到「禮品目錄」新增。</p>
            ) : (
              <select
                value={selectedGiftKey}
                onChange={(e) => setSelectedGiftKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
              >
                <option value="">請選擇禮品</option>
                {giftOptions.map(g => (
                  <option key={g.id} value={g.key}>{g.name}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">禮品會直接附加到此訂單（狀態：已隨單），打包出貨時請放入實物。</p>
            {giftError && <p className="text-sm text-red-600 mt-2">{giftError}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowGiftModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                取消
              </button>
              <button
                onClick={handleAttachGift}
                disabled={attachingGift || giftOptions.length === 0}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition disabled:opacity-50"
              >
                {attachingGift ? '附加中…' : '確認附加'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrderDetail;
